// src/components/Header.tsx
import React, { useContext } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  IconButton, 
  Menu, 
  MenuItem, 
  Avatar, 
  useMediaQuery, 
  Box,
  Tooltip,
  Divider,
  Theme,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import SettingsIcon from '@mui/icons-material/Settings';
import { AuthContext } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useThemeContext, getThemeIcon } from '../context/ThemeProvider';
import LoyaltyIcon from '@mui/icons-material/Loyalty';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import DeliveryDiningIcon from '@mui/icons-material/DeliveryDining'; // 👈 add this at the top


const TB_GOLD = '#FFB300';
const TB_GOLD_DARK = '#FF8F00';
interface HeaderProps {
  isSidebarOpen: boolean;
  setSidebarOpen: (open: boolean) => void;
}

const Header: React.FC<HeaderProps> = ({ isSidebarOpen, setSidebarOpen }) => {
  const { themeMode, toggleThemeMode } = useThemeContext();
  const { user, logout } = useContext(AuthContext);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = React.useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const isTabletOrMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down('lg'));

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleSettingsMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  const handleSettingsMenuClose = () => {
    setSettingsAnchorEl(null);
  };

  const handleSignOut = () => {
    logout();
    navigate('/login');
  };

  const settingsItems = [
  { text: 'Loyalty', path: '/settings/loyalty', icon: LoyaltyIcon },
  { text: 'Store Credit', path: '/settings/store-credit', icon: CreditCardIcon },
  { text: 'Shipping Rates', path: '/settings/shipping', icon: LocalShippingIcon },
  { text: 'Delivery Settings', path: '/settings/delivery-settings', icon: DeliveryDiningIcon }, // 👈 new
  { text: 'Delivery Zones', path: '/settings/delivery-zones', icon: DeliveryDiningIcon }, // 👈 new
];

  return (
    <AppBar
      position="fixed"
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backdropFilter: 'blur(10px)',
        background: (theme) => {
          switch (themeMode) {
            case 'dark':
              return 'rgba(15, 23, 42, 0.85)';
            case 'vivid':
              return 'rgba(255, 255, 255, 0.95)';
            case 'blackout':
              return 'rgba(0, 0, 0, 0.9)';
            default: // light
              return 'rgba(255, 255, 255, 0.85)';
          }
        },
        boxShadow: 'none',
        borderBottom: (theme) => `1px solid ${
          themeMode === 'blackout' 
            ? 'rgba(255, 255, 255, 0.12)' 
            : theme.palette.divider
        }`,
        transition: 'background 0.3s ease, border 0.3s ease',
      }}
    >
      <Toolbar sx={{ 
        display: 'flex', 
        justifyContent: 'space-between',
        gap: 2,
        minHeight: '64px !important',
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
  {isTabletOrMobile && (
    <IconButton 
      edge="start" 
      onClick={() => setSidebarOpen(!isSidebarOpen)}
      sx={{ color: 'text.primary' }}
    >
      <MenuIcon />
    </IconButton>
  )}
  <Typography 
    variant="h6" 
    noWrap 
    sx={{ 
      fontWeight: 700,
      letterSpacing: '-0.5px',
      background: (theme) => {
        switch (themeMode) {
          case 'vivid':
            return `linear-gradient(45deg, ${TB_GOLD} 0%, ${TB_GOLD_DARK} 100%)`;
          case 'dark':
            return `linear-gradient(45deg, ${TB_GOLD} 0%, ${TB_GOLD_DARK} 50%, #FFFFFF 100%)`;
          case 'blackout':
            return `linear-gradient(45deg, ${TB_GOLD} 0%, ${TB_GOLD_DARK} 100%)`;
          default: // light
            return `linear-gradient(45deg, ${TB_GOLD_DARK} 0%, ${TB_GOLD} 100%)`;
        }
      },
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text',
      textFillColor: 'transparent',
      filter: themeMode === 'dark' ? 'saturate(1.2)' : 'none',
    }}
  >
    ThunderBird Dashboard
  </Typography>
</Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Tooltip title={`Theme: ${themeMode}`}>
            <IconButton 
              onClick={toggleThemeMode} 
              sx={{ 
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: themeMode === 'blackout' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              {getThemeIcon(themeMode)}
            </IconButton>
          </Tooltip>

          <Tooltip title="Settings">
            <IconButton 
              onClick={handleSettingsMenuOpen}
              sx={{ 
                color: 'text.primary',
                '&:hover': {
                  backgroundColor: themeMode === 'blackout' 
                    ? 'rgba(255, 255, 255, 0.1)' 
                    : 'rgba(0, 0, 0, 0.05)'
                }
              }}
            >
              <SettingsIcon />
            </IconButton>
          </Tooltip>
          <Menu
            anchorEl={settingsAnchorEl}
            open={Boolean(settingsAnchorEl)}
            onClose={handleSettingsMenuClose}
            sx={{ 
              '& .MuiPaper-root': { 
                mt: 1.5, 
                minWidth: 200,
                boxShadow: (theme) => theme.shadows[3],
                background: (theme) => 
                  themeMode === 'blackout' 
                    ? 'rgba(20, 20, 20, 0.95)' 
                    : theme.palette.background.paper,
                border: (theme) => 
                  `1px solid ${
                    themeMode === 'blackout' 
                      ? 'rgba(255, 255, 255, 0.12)' 
                      : theme.palette.divider
                  }`
              }
            }}
          >
            {settingsItems.map((item) => (
              <MenuItem 
                key={item.text}
                onClick={() => {
                  navigate(item.path);
                  handleSettingsMenuClose();
                }}
                sx={{ 
                  py: 1,
                  '&:hover': {
                    backgroundColor: themeMode === 'blackout' 
                      ? 'rgba(255, 255, 255, 0.08)' 
                      : undefined
                  }
                }}
              >
                <ListItemIcon>
                  <item.icon fontSize="small" />
                </ListItemIcon>
                <ListItemText>{item.text}</ListItemText>
              </MenuItem>
            ))}
          </Menu>

          {user && (
            <>
              <Avatar
                sx={{ 
                  bgcolor: 'primary.main', 
                  color: 'primary.contrastText',
                  width: 36, 
                  height: 36,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'transform 0.2s',
                  '&:hover': {
                    transform: 'scale(1.1)'
                  }
                }}
                onClick={handleMenuOpen}
              >
                {user.name?.charAt(0).toUpperCase()}
              </Avatar>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                sx={{ 
                  '& .MuiPaper-root': { 
                    mt: 1.5, 
                    minWidth: 200,
                    boxShadow: (theme) => theme.shadows[3],
                    background: (theme) => 
                      themeMode === 'blackout' 
                        ? 'rgba(20, 20, 20, 0.95)' 
                        : theme.palette.background.paper,
                    border: (theme) => 
                      `1px solid ${
                        themeMode === 'blackout' 
                          ? 'rgba(255, 255, 255, 0.12)' 
                          : theme.palette.divider
                      }`
                  }
                }}
              >
                <Box sx={{ px: 2, py: 1 }}>
                  <Typography variant="subtitle2">{user.name}</Typography>
                  <Typography variant="body2" color="textSecondary">{user.email}</Typography>
                </Box>
                <Divider sx={{ 
                  borderColor: themeMode === 'blackout' 
                    ? 'rgba(255, 255, 255, 0.12)' 
                    : undefined 
                }} />
                <MenuItem 
                  onClick={handleSignOut} 
                  sx={{ 
                    py: 1,
                    '&:hover': {
                      backgroundColor: themeMode === 'blackout' 
                        ? 'rgba(255, 255, 255, 0.08)' 
                        : undefined
                    }
                  }}
                >
                  Sign Out
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default Header;